import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { 
  CircularProgress, ClickAwayListener, 
  Divider, List, ListItem, Paper, Popper, Typography 
} from '@material-ui/core'

import { ObjetoAvatar } from 'components'

import { useSearchService } from 'hooks/useSearchService'
import useRouter from 'utils/useRouter'
import { cutMaxLength } from 'utils/helpers'
import useStyles from '../SearchBar.styles'
import Cesta from '../../../cesta.png'

const searchResponseType = {
  CASOS: 'casos',
  VESTIGIOS: 'vestigios',
  CADAVERES: 'cadaveres',
  PERICIANDOS: 'periciandos',
  VEICULOS: 'veiculos',
  EXAMES_REALIZADOS: 'examesRealizados',
  REQUISICOES: 'requisicoes',
  LOTE: 'lote',
  LACRE: 'lacre',
}

const typeLabel = {
  [searchResponseType.CASOS]: 'Caso',
  [searchResponseType.VESTIGIOS]: 'Vestígio',
  [searchResponseType.CADAVERES]: 'Cadáver',
  [searchResponseType.PERICIANDOS]: 'Periciando',
  [searchResponseType.VEICULOS]: 'Veículo',
  [searchResponseType.EXAMES_REALIZADOS]: 'Exame',
  [searchResponseType.REQUISICOES]: 'Requisição',
  [searchResponseType.LOTE]: 'Lote',
  [searchResponseType.LACRE]: 'Lacre',
}

// Mapeamento Unificado para o ObjetoAvatar do SIGEP! 🛡️✨🚀
const itemAvatarTypes = {
  [searchResponseType.CASOS]: 'CASO',
  [searchResponseType.VESTIGIOS]: 'VESTIGIO',
  [searchResponseType.PERICIANDOS]: 'PERICIANDO',
  [searchResponseType.CADAVERES]: 'CADAVER',
  [searchResponseType.VEICULOS]: 'VEICULO',
  [searchResponseType.EXAMES_REALIZADOS]: 'EXAME',
  [searchResponseType.REQUISICOES]: 'REQUISICAO',
  [searchResponseType.LOTE]: null, // Renderiza a imagem da cesta em fallback
  [searchResponseType.LACRE]: 'LACRE',
}

export const ResultadoPesquisa = (props) => {
  const { openSearchPopover, searchRef, debouncedSearchValue, setOpenSearchPopover } = props
  const searchService = useSearchService()
  const classes = useStyles()
  const [searchResponses, setSearchResponses] = useState([])
  const [processandoResponse, setProcessandoResponse] = useState(false)
  const router = useRouter()

  useEffect(() => {
    if (debouncedSearchValue?.length > 2) {
      searchService.search(debouncedSearchValue, setSearchResponses, setProcessandoResponse)
    }
  }, [debouncedSearchValue])

  const handleClickSearchItem = (id, type) => {
    switch (type) {
      case searchResponseType.CASOS:
        router.history.push(`/casos/caso/detalhar/${id}`)
        break
      case searchResponseType.VESTIGIOS:
        router.history.push(`/casos/caso/vestigio/detalhar/${id}`)
        break
      case searchResponseType.PERICIANDOS:
        router.history.push(`/casos/caso/periciando/detalhar/${id}`)
        break
      case searchResponseType.VEICULOS:
        router.history.push(`/casos/caso/veiculo/detalhar/${id}`)
        break
      case searchResponseType.EXAMES_REALIZADOS:
        router.history.push(`/casos/caso/solicitacao/exameSolicitado/detalhar/${id}`)
        break
      case searchResponseType.REQUISICOES:
        router.history.push(`/requisicoes/requisicao/detalhar/${id}`)
        break
      case searchResponseType.LOTE:
        router.history.push(`/vestigios/lote/${id}`)
        break
      case searchResponseType.LACRE:
        router.history.push(`/casos/caso/vestigio/detalhar/${id}`)
        break
      case searchResponseType.CADAVERES:
      default:
        router.history.push(`/casos/caso/cadaver/detalhar/${id}`)
    }
    setOpenSearchPopover(false)
  }

  const SearchListItem = ({ label, sublabel, id, sei, type }) => {
    const avatarType = itemAvatarTypes[type]
    
    // Constrói a label dinâmica permitindo leitura completa sem badge
    let subtitleText = typeLabel[type]
    if (type === searchResponseType.CASOS && sei) {
      subtitleText = `Caso: SEI ${sei}`
    }

    return (
      <ListItem 
        button 
        className={classes.searchListItem} 
        onClick={() => handleClickSearchItem(id, type)}
      >
        {/* Componente Único de Avatar Globalizado do SIGEP! 🛡️🛸🏆 */}
        <ObjetoAvatar 
          tipo={avatarType} 
          style={{ marginRight: '16px', flexShrink: 0 }}
        >
          {!avatarType && <img src={Cesta} className={classes.imgLote} alt="Lote" />}
        </ObjetoAvatar>
        
        <div className={classes.searchItemContent}>
          <Typography className={classes.searchItemTitle} noWrap>
            {label || '(Sem identificador)'}
          </Typography>
          <div className={classes.searchItemSub}>
            <Typography variant="caption" style={{ fontWeight: 500 }} noWrap>
              {subtitleText}
            </Typography>
          </div>
          {sublabel && (
            <Typography className={classes.searchItemDesc} noWrap>
              {cutMaxLength(sublabel, 60)}
            </Typography>
          )}
        </div>
      </ListItem>
    )
  }

  const hasResults = searchResponses && Object.values(searchResponses).some(arr => Array.isArray(arr) && arr.length > 0)

  return (
    <Popper
      anchorEl={searchRef.current}
      className={classes.searchPopper}
      open={openSearchPopover}
      transition
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <ClickAwayListener onClickAway={() => setOpenSearchPopover(false)}>
        <Paper className={classes.searchPopperContent}>
          {processandoResponse ? (
            <div className={classes.carregando}>
              <CircularProgress size={28} disableShrink />
            </div>
          ) : (
            <div>
              {hasResults ? (
                <div>
                  <div className={classes.popperHeader}>
                    <Typography className={classes.popperTitle}>
                      Resultados da Busca
                    </Typography>
                  </div>
                  <Divider style={{ opacity: 0.5 }} />
                  <List className={classes.searchList} disablePadding>
                    {searchResponses.casos?.map((search) => (
                      <SearchListItem key={`caso-${search.id}`} label={search.identificador} sublabel={search.descricao} id={search.id} sei={search.processoSei} type={searchResponseType.CASOS} />
                    ))}
                    {searchResponses.cadaveres?.map((search) => (
                      <SearchListItem key={`cadaver-${search.id}`} label={search.nic} sublabel={search.descricao_geral} id={search.id} type={searchResponseType.CADAVERES} />
                    ))}
                    {searchResponses.vestigios?.map((search) => (
                      <SearchListItem key={`vestigio-${search.id}`} label={search.identificador} sublabel={search.descricao} id={search.id} type={searchResponseType.VESTIGIOS} />
                    ))}
                    {searchResponses.periciandos?.map((search) => (
                      <SearchListItem key={`periciando-${search.id}`} label={search.cpf || search.nome} sublabel={search.nome} id={search.id} type={searchResponseType.PERICIANDOS} />
                    ))}
                    {searchResponses.veiculos?.map((search) => (
                      <SearchListItem key={`veiculo-${search.id}`} label={search.identificador?.trim() ? search.identificador : search.placa?.trim() || '(Sem placa)'} sublabel={search.descricao} id={search.id} type={searchResponseType.VEICULOS} />
                    ))}
                    {searchResponses.examesRealizados?.map((search) => (
                      <SearchListItem key={`exame-${search.id}`} label={`${search.numeroExame}`} sublabel={search.exSol?.tipoEx?.nome} id={search.exSol?.id} type={searchResponseType.EXAMES_REALIZADOS} />
                    ))}
                    {searchResponses.requisicoes?.map((search) => (
                      <SearchListItem key={`req-${search.id}`} label={`${search.identificador}`} sublabel={search.resumo_ocorrencia} id={search.id} type={searchResponseType.REQUISICOES} />
                    ))}
                    {searchResponses.lote?.map((search) => (
                      <SearchListItem key={`lote-${search.id}`} label={`Lote #${search.numero}`} sublabel={'Lote de vestígios em trâmite'} id={search.id} type={searchResponseType.LOTE} />
                    ))}
                    {searchResponses.lacre?.map((search) => (
                      <SearchListItem key={`lacre-${search.id}`} label={`Lacre: ${search.lacre}`} sublabel={`Vestígio ${search.identificador}`} id={search.id} type={searchResponseType.LACRE} />
                    ))}
                  </List>
                </div>
              ) : (
                <div style={{ padding: '24px', textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhum resultado correspondente.
                  </Typography>
                </div>
              )}
            </div>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}

export default ResultadoPesquisa
